/* global google */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GoogleMap, InfoWindowF, MarkerF } from '@react-google-maps/api';
import { MapPin, Navigation, ZoomIn, ZoomOut } from 'lucide-react';

import type { Park } from '../types';
import { useApp } from '../context/PetParksContext';

import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { EmptyState } from './EmptyState';

interface MapViewProps {
  parks: Park[];
  onParkSelect: (park: Park) => void;
}

type LatLng = { lat: number; lng: number };

export const MapView: React.FC<MapViewProps> = ({ parks, onParkSelect }) => {
  const { t, userLocation, searchCenter, googleMaps } = useApp();

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [activeParkId, setActiveParkId] = useState<string | null>(null);

  const defaultCenter: LatLng = useMemo(() => {
    if (searchCenter) return searchCenter;
    if (userLocation) return userLocation;
    if (parks[0]?.coordinates) return parks[0].coordinates;
    return { lat: 40.7128, lng: -74.006 }; // fallback
  }, [parks, searchCenter, userLocation]);

  // Fit map to show the current center + all parks (so user always sees something)
  useEffect(() => {
    if (!googleMaps.isLoaded) return;
    if (!map) return;
    if (parks.length === 0) return;

    const g = window.google;
    const bounds = new g.maps.LatLngBounds();
    bounds.extend(defaultCenter);
    parks.forEach((p) => bounds.extend(p.coordinates));
    map.fitBounds(bounds);
  }, [defaultCenter, googleMaps.isLoaded, map, parks]);

  const handleCenterOnUser = useCallback(() => {
    if (!map || !userLocation) return;
    map.panTo(userLocation);
    map.setZoom(Math.max(map.getZoom() ?? 14, 14));
  }, [map, userLocation]);

  const handleZoomIn = useCallback(() => {
    if (!map) return;
    map.setZoom((map.getZoom() ?? 12) + 1);
  }, [map]);

  const handleZoomOut = useCallback(() => {
    if (!map) return;
    map.setZoom((map.getZoom() ?? 12) - 1);
  }, [map]);

  if (parks.length === 0) {
    return (
      <EmptyState
        type="no-parks"
        title={t('noParksNearby')}
        description={t('tryAdjustingFilters')}
      />
    );
  }

  if (!googleMaps.apiKey) {
    return (
      <EmptyState
        type="welcome"
        title={t('googleMapsNotConfigured')}
        description={t('googleMapsNotConfiguredDescription')}
      />
    );
  }

  if (googleMaps.loadError) {
    return (
      <EmptyState
        type="welcome"
        title={t('failedToLoadGoogleMaps')}
        description={t('failedToLoadGoogleMapsDescription')}
      />
    );
  }

  if (!googleMaps.isLoaded) {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-lg bg-secondary">
        <div className="text-sm text-muted-foreground">{t('loadingMap')}</div>
      </div>
    );
  }

  return (
    <div className="relative h-full min-h-[280px] sm:min-h-0 w-full min-w-0 overflow-hidden rounded-lg bg-secondary shadow-inner">
      <GoogleMap
        center={defaultCenter}
        zoom={13}
        mapContainerClassName="h-full w-full"
        onLoad={(m) => setMap(m)}
        options={{
          clickableIcons: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        }}
        onClick={() => setActiveParkId(null)}
      >
        {/* User location */}
        {userLocation && (
          <MarkerF
            position={userLocation}
            title={t('yourLocation')}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#3b82f6',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            }}
          />
        )}

        {/* Parks */}
        {parks.map((park) => (
          <MarkerF
            key={park.id}
            position={park.coordinates}
            title={park.name}
            onClick={() => {
              setActiveParkId(park.id);
              onParkSelect(park);
            }}
          />
        ))}

        {activeParkId &&
          (() => {
            const park = parks.find((p) => p.id === activeParkId);
            if (!park) return null;
            return (
              <InfoWindowF
                position={park.coordinates}
                onCloseClick={() => setActiveParkId(null)}
              >
                <div className="min-w-[140px] max-w-[260px]">
                  <div className="mb-1 font-semibold break-words">
                    {park.name}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                    <span>{park.distance} km</span>
                    <span>•</span>
                    <Badge variant="secondary" className="h-4 text-xs shrink-0">
                      ⭐ {park.rating}
                    </Badge>
                  </div>
                </div>
              </InfoWindowF>
            );
          })()}
      </GoogleMap>

      {/* Map Controls - larger touch targets on mobile */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-2">
        <Button
          size="icon"
          variant="default"
          className="h-11 w-11 min-h-11 min-w-11 rounded-full shadow-lg sm:h-10 sm:w-10 sm:min-h-10 sm:min-w-10 touch-manipulation cursor-pointer"
          title={t('centerOnCurrentLocation')}
          onClick={handleCenterOnUser}
          disabled={!userLocation}
        >
          <Navigation className="h-5 w-5 sm:h-5 sm:w-5" />
        </Button>
      </div>

      {/* Zoom Controls - larger touch targets on mobile */}
      <div className="absolute right-3 top-3 sm:right-4 sm:top-4 flex flex-col gap-2">
        <Button
          size="icon"
          variant="default"
          className="h-11 w-11 min-h-11 min-w-11 rounded-full shadow-lg sm:h-10 sm:w-10 sm:min-h-10 sm:min-w-10 touch-manipulation cursor-pointer"
          title={t('zoomIn')}
          onClick={handleZoomIn}
          disabled={!map}
        >
          <ZoomIn className="h-5 w-5 sm:h-5 sm:w-5" />
        </Button>
        <Button
          size="icon"
          variant="default"
          className="h-11 w-11 min-h-11 min-w-11 rounded-full shadow-lg sm:h-10 sm:w-10 sm:min-h-10 sm:min-w-10 touch-manipulation cursor-pointer"
          title={t('zoomOut')}
          onClick={handleZoomOut}
          disabled={!map}
        >
          <ZoomOut className="h-5 w-5 sm:h-5 sm:w-5" />
        </Button>
      </div>

      {/* Attribution / status - compact on mobile */}
      <div className="absolute bottom-2 left-2 max-w-[85%] rounded bg-white/90 px-2 py-1 text-[10px] sm:text-xs text-gray-600 shadow dark:bg-black/50 dark:text-gray-300 truncate">
        <MapPin className="mr-1 inline h-3 w-3 shrink-0" />
        <span className="truncate">
          {parks.length} {t('parksCount')}
        </span>
      </div>
    </div>
  );
};
